/**
 * Semantic Styling Utility Functions
 *
 * These functions convert semantic type-safe values into Tailwind CSS classes,
 * providing consistency and preventing errors. Designed to be LLM-friendly with
 * clear names and predictable behavior.
 */

import type {
  BorderStyle,
  ColorVariant,
  PaddingSize,
  RoundedSize,
  SectionBackground,
  SectionPaddingSize,
  ShadowSize,
} from "../types/styling";

/**
 * Container max width constant - used consistently across all sections
 */
export const CONTAINER_MAX_WIDTH = "max-w-[1200px]" as const;

export function getShadowClass(size: ShadowSize): string {
  if (size === "none") return "";
  return `shadow-${size}`;
}

export function getRoundedClass(size: RoundedSize): string {
  return `rounded-${size}`;
}

export function getPaddingClass(size: PaddingSize): string {
  const paddingMap: Record<PaddingSize, string> = {
    none: "",
    sm: "p-4",
    md: "p-6",
    lg: "p-9",
    xl: "p-10",
  };
  return paddingMap[size];
}

export function getBorderClass(style: BorderStyle): string {
  const borderMap: Record<BorderStyle, string> = {
    none: "",
    default: "border border-border",
    thick: "border-2 border-border",
  };
  return borderMap[style];
}

export function getSectionPaddingClass(size: SectionPaddingSize): string {
  const paddingMap: Record<SectionPaddingSize, string> = {
    sm: "py-10 lg:py-16",
    md: "py-14 lg:py-20",
    lg: "py-[70px] lg:py-[100px]",
    xl: "py-24 lg:py-32",
    none: "",
  };
  return paddingMap[size];
}

const BASE_COLORS = new Set(["base-100", "base-200", "base-300"]);
const SEMANTIC_COLORS = new Set([
  "primary",
  "secondary",
  "accent",
  "neutral",
  "success",
  "warning",
  "error",
  "info",
]);

// Converge legacy DaisyUI color names to the intent-named Tier-2 tokens (ADR-0080),
// mirroring the static color-rename codemod. secondary/accent/neutral/state colors
// still collide with DaisyUI's own token names, so they pass through unchanged until
// the DaisyUI-removal PR joins them to the semantic layer.
const INTENT_TOKEN_MAP: Record<string, string> = {
  "base-100": "surface",
  "base-200": "bg",
  "base-300": "bg",
  primary: "brand",
};

function toIntentToken(name: string): string {
  return INTENT_TOKEN_MAP[name] ?? name;
}

/**
 * Converts any SectionBackground to its Tailwind class
 * Uses bg-grad-{name} for gradients, bg-{name} for base/semantic colors.
 * "none" (ADR-0091 kit-adoption escape hatch) emits no class — the migrated
 * root owns its own background via className/style.
 */
export function getSectionBackgroundClass(bg: SectionBackground | string): string {
  if (bg === "none") return "";
  if (BASE_COLORS.has(bg) || SEMANTIC_COLORS.has(bg)) {
    return `bg-${toIntentToken(bg)}`;
  }
  return `bg-grad-${bg}`;
}

export function getVariantColorClass(variant: ColorVariant): string {
  const token = toIntentToken(variant);
  return `bg-${token} text-${token}-content`;
}

export function getHoverClass(enabled: boolean): string {
  return enabled ? "transition-all duration-200 hover:shadow-xl" : "";
}

export function getContainerClass(): string {
  return `container mx-auto ${CONTAINER_MAX_WIDTH} px-6`;
}
